import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

export type Card = Tables<'cards'>;

export interface CardPrint {
  id: string;
  card_id: string;
  treatment: string;
  rarity: string | null;
  collector_number: string | null;
  image_url: string | null;
  tcgplayer_id: number | null;
  price_usd: number | null;
  price_usd_foil: number | null;
  price_usd_updated: string | null;
  available_in_packs: boolean | null;
  notes: string | null;
  created_at: string;
}

export type CardWithPrints = Card & { prints?: CardPrint[] };

export function useCards(filters?: {
  ink_color?: string;
  rarity?: string;
  set_name?: string;
  search?: string;
  sort?: 'name' | 'set' | 'price_asc' | 'price_desc' | 'rarity';
}) {
  return useQuery({
    queryKey: ['cards', filters],
    queryFn: async () => {
      const PAGE_SIZE = 1000;
      let allData: Card[] = [];
      let page = 0;
      let hasMore = true;

      while (hasMore) {
        let query = supabase.from('cards').select('*');
        if (filters?.ink_color) query = query.eq('ink_color', filters.ink_color as any);
        if (filters?.rarity) query = query.eq('rarity', filters.rarity as any);
        if (filters?.set_name) query = query.eq('set_name', filters.set_name);
        if (filters?.search) query = query.ilike('name', `%${filters.search}%`);

        query = query.order('name').range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        const { data, error } = await query;
        if (error) throw error;
        
        allData = allData.concat(data as Card[]);
        hasMore = (data?.length || 0) === PAGE_SIZE;
        page++;
      }

      return allData;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCardSets() {
  return useQuery({
    queryKey: ['card-sets'],
    queryFn: async () => {
      // Prefer the canonical sets table; fall back to scanning cards if empty.
      const { data: setRows, error: setErr } = await supabase
        .from('sets')
        .select('name')
        .order('released_at', { ascending: false, nullsFirst: false });
      if (setErr) throw setErr;
      if (setRows && setRows.length > 0) {
        return [...new Set(setRows.map(r => r.name))];
      }

      // Fallback: paginate through cards so we don't miss sets past the 1000-row default.
      const PAGE_SIZE = 1000;
      let page = 0;
      const seen = new Set<string>();
      while (true) {
        const { data, error } = await supabase
          .from('cards')
          .select('set_name')
          .order('set_name')
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        for (const r of data || []) seen.add(r.set_name);
        if (!data || data.length < PAGE_SIZE) break;
        page++;
      }
      return [...seen].sort();
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function useCard(id: string | null) {
  return useQuery({
    queryKey: ['card', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase.from('cards').select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      return data as Card | null;
    },
    enabled: !!id,
  });
}

export function useCardPrints(cardId: string | null) {
  return useQuery({
    queryKey: ['card-prints', cardId],
    queryFn: async () => {
      if (!cardId) return [];
      const { data, error } = await supabase
        .from('card_prints')
        .select('*')
        .eq('card_id', cardId)
        .order('treatment');
      if (error) throw error;
      return (data || []) as CardPrint[];
    },
    enabled: !!cardId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch every card_prints row (paginated). Returns a Map of card_id -> prints[]
 * so callers can look up the real, latest market price for any card in O(1).
 */
export function useAllPrints() {
  return useQuery({
    queryKey: ['all-card-prints'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const PAGE_SIZE = 1000;
      let page = 0;
      let all: CardPrint[] = [];
      while (true) {
        const { data, error } = await supabase
          .from('card_prints')
          .select('*')
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        all = all.concat((data || []) as CardPrint[]);
        if (!data || data.length < PAGE_SIZE) break;
        page++;
      }
      const byCard = new Map<string, CardPrint[]>();
      for (const p of all) {
        const arr = byCard.get(p.card_id) || [];
        arr.push(p);
        byCard.set(p.card_id, arr);
      }
      return byCard;
    },
  });
}

/** Pick the best representative price for a card from its prints. */
export function getBestPrintPrice(prints: CardPrint[] | undefined): number | null {
  if (!prints?.length) return null;
  const order = ['normal', 'cold_foil', 'holofoil', 'rainbow_foil', 'inkwash_foil', 'glimmer_foil', 'epic', 'iconic', 'enchanted', 'iridescent', 'promo'];
  for (const t of order) {
    const hit = prints.find(p => p.treatment === t && p.price_usd != null);
    if (hit) return Number(hit.price_usd);
  }
  const any = prints.find(p => p.price_usd != null);
  return any?.price_usd != null ? Number(any.price_usd) : null;
}


export function useSearchCards(name: string, subtitle?: string) {
  return useQuery({
    queryKey: ['card-search', name, subtitle],
    queryFn: async () => {
      let query = supabase.from('cards').select('*').ilike('name', `%${name}%`);
      if (subtitle) query = query.ilike('subtitle', `%${subtitle}%`);
      const { data, error } = await query.limit(5);
      if (error) throw error;
      return data as Card[];
    },
    enabled: name.length > 0,
  });
}

// Get the best price for a card from its prints
export function getCardPriceFromPrints(prints: CardPrint[]): { normal: number | null; foil: number | null; best: number | null } {
  const normalPrint = prints.find(p => p.treatment === 'normal');
  const foilPrint = prints.find(p => p.treatment === 'cold_foil');
  const enchantedPrint = prints.find(p => p.treatment === 'enchanted');
  const epicPrint = prints.find(p => p.treatment === 'epic');

  const normal = normalPrint?.price_usd ?? null;
  const foil = foilPrint?.price_usd ?? enchantedPrint?.price_usd ?? epicPrint?.price_usd ?? null;
  const best = normal ?? foil;

  return { normal, foil, best };
}

export const TREATMENT_LABELS: Record<string, { label: string; emoji: string }> = {
  normal: { label: 'Normal', emoji: '🃏' },
  cold_foil: { label: 'Cold Foil', emoji: '✨' },
  holofoil: { label: 'Holofoil', emoji: '💫' },
  rainbow_foil: { label: 'Rainbow Foil', emoji: '🌈' },
  enchanted: { label: 'Enchanted', emoji: '🌟' },
  inkwash_foil: { label: 'Inkwash Foil', emoji: '🎨' },
  epic: { label: 'Epic', emoji: '🔥' },
  iconic: { label: 'Iconic', emoji: '⭐' },
  glimmer_foil: { label: 'Glimmer Foil', emoji: '💎' },
  promo: { label: 'Promo', emoji: '🏆' },
  iridescent: { label: 'Iridescent', emoji: '🦋' },
};
